import type { JSX } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { SaveViewButton } from '~publish/components/MainSidebar/components/SavedViewsSection'

import { CreatePageLayout } from './CreatePageLayout'
import type { CreatePageTab } from './CreatePageLayout'
import { FeedsNavigation } from './components/FeedsNavigation'
import { UpgradePathForCreate } from './components/UpgradePathForCreate'
import { useCurrentOrganization } from '../../contexts/AccountContext'

const TABS = ['ideas', 'templates', 'feeds'] as const

type CreateTab = (typeof TABS)[number]

const CREATE_TABS: CreatePageTab[] = [
  { value: 'ideas', label: 'Ideas', to: '/create/ideas' },
  { value: 'templates', label: 'Templates', to: '/create/templates' },
  { value: 'feeds', label: 'Feeds', to: '/create/feeds' },
]

function isValidTab(value: string | undefined): value is CreateTab {
  return typeof value === 'string' && TABS.some((tab) => tab === value)
}

function getTabFromPath(pathname: string): CreateTab {
  const segments = pathname.split('/')
  const segment = segments[segments.indexOf('create') + 1]
  if (isValidTab(segment)) {
    return segment
  }
  return 'ideas'
}

export function CreatePage(): JSX.Element | null {
  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrg = currentOrganization?.isOneBufferOrganization
  const hasContentFeature =
    currentOrganization?.entitlements.includes('content') ?? false
  const location = useLocation()
  const currentTab = getTabFromPath(location.pathname)

  if (!currentOrganization?.id) {
    return null
  }

  if (!isOneBufferOrg || !hasContentFeature) {
    return <UpgradePathForCreate />
  }

  return (
    <CreatePageLayout
      currentTab={currentTab}
      tabs={CREATE_TABS}
      titleAdornments={<SaveViewButton />}
      actions={<FeedbackWidget id="content-feeds-1" source="create" />}
      belowHeader={<FeedsNavigation />}
    >
      <Outlet />
    </CreatePageLayout>
  )
}
